import { useQueryClient, useMutation, UseMutationResult } from 'react-query';
import request from '../request';
import ServerErrorHandler from '../../ErrorHandler';
import { permissionQueryKeys } from './permission-queries';

interface PermissionGroupInput {
  documentId: string;
  name: string;
}

interface UpdatePermissionGroupInput {
  documentId: string;
  permissionGroupId: number;
  name: string;
}

interface DeletePermissionGroupInput {
  documentId: string;
  permissionGroupId: number;
}

interface AddUserToGroupInput {
  documentId: string;
  permissionGroupId: number;
  userId: number;
}

interface RemoveUserFromGroupInput {
  documentId: string;
  permissionGroupId: number;
  userId: number;
}

interface ChangeSectionPermissionInput {
  documentId: string;
  sectionSuperId: string;
  permission: string;
  userId: number;
}

interface RemoveSectionPermissionInput {
  documentId: string;
  sectionSuperId: string;
  permission: string;
  userId: number;
}

function useInvalidatePermissions() {
  const queryClient = useQueryClient();
  return (documentId: string) => {
    queryClient.invalidateQueries([permissionQueryKeys.permission, documentId]);
    queryClient.invalidateQueries([permissionQueryKeys.documentPermissions, documentId]);
  };
}

export function useCreatePermissionGroup(): UseMutationResult<unknown, unknown, PermissionGroupInput> {
  const invalidate = useInvalidatePermissions();
  return useMutation(
    async ({ documentId, name }: PermissionGroupInput) => {
      const res = await request.post('/gaby/permission-groups', { name, documentSuperId: documentId });
      return res?.data;
    },
    {
      onSuccess: (_data, variables) => {
        invalidate(variables.documentId);
      },
      onError: ServerErrorHandler,
    }
  );
}

export function useUpdatePermissionGroup(): UseMutationResult<unknown, unknown, UpdatePermissionGroupInput> {
  const invalidate = useInvalidatePermissions();
  return useMutation(
    async ({ documentId, permissionGroupId, name }: UpdatePermissionGroupInput) => {
      const res = await request.put(`/gaby/permission-groups/${permissionGroupId}`, { name, documentSuperId: documentId });
      return res?.data;
    },
    {
      onSuccess: (_data, variables) => {
        invalidate(variables.documentId);
      },
      onError: ServerErrorHandler,
    }
  );
}

export function useDeletePermissionGroup(): UseMutationResult<unknown, unknown, DeletePermissionGroupInput> {
  const invalidate = useInvalidatePermissions();
  return useMutation(
    async ({ permissionGroupId }: DeletePermissionGroupInput) => {
      await request.del(`/gaby/permission-groups/${permissionGroupId}`, null);
    },
    {
      onSuccess: (_data, variables) => {
        invalidate(variables.documentId);
      },
      onError: ServerErrorHandler,
    }
  );
}

export function useAddUserToPermissionGroup(): UseMutationResult<unknown, unknown, AddUserToGroupInput> {
  const invalidate = useInvalidatePermissions();
  return useMutation(
    async ({ permissionGroupId, userId }: AddUserToGroupInput) => {
      const res = await request.post(`/gaby/permission-groups/${permissionGroupId}/users`, { userId });
      return res?.data;
    },
    {
      onSuccess: (_data, variables) => {
        invalidate(variables.documentId);
      },
      onError: ServerErrorHandler,
    }
  );
}

export function useRemoveUserFromPermissionGroup(): UseMutationResult<unknown, unknown, RemoveUserFromGroupInput> {
  const invalidate = useInvalidatePermissions();
  return useMutation(
    async ({ permissionGroupId, userId }: RemoveUserFromGroupInput) => {
      await request.del(`/gaby/permission-groups/${permissionGroupId}/users/${userId}`, null);
    },
    {
      onSuccess: (_data, variables) => {
        invalidate(variables.documentId);
      },
      onError: ServerErrorHandler,
    }
  );
}

export function useChangeSectionPermission(): UseMutationResult<unknown, unknown, ChangeSectionPermissionInput> {
  const invalidate = useInvalidatePermissions();
  return useMutation(
    async ({ sectionSuperId, permission, userId }: ChangeSectionPermissionInput) => {
      const res = await request.put(`/gaby/sections/${sectionSuperId}/permissions/${permission}/users`, { userId, permission });
      return res?.data;
    },
    {
      onSuccess: (_data, variables) => {
        invalidate(variables.documentId);
      },
      onError: ServerErrorHandler,
    }
  );
}

export function useRemoveSectionPermission(): UseMutationResult<unknown, unknown, RemoveSectionPermissionInput> {
  const invalidate = useInvalidatePermissions();
  return useMutation(
    async ({ sectionSuperId, permission, userId }: RemoveSectionPermissionInput) => {
      await request.del(`/gaby/sections/${sectionSuperId}/permissions/${permission}/users/${userId}`, null);
    },
    {
      onSuccess: (_data, variables) => {
        invalidate(variables.documentId);
      },
      onError: ServerErrorHandler,
    }
  );
}
